/* eslint-disable import/no-cycle */
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import { useCallback, useEffect, useState } from 'react';
import Image from '@base/FutureImage';
import Cross from 'aquilla/icons/misc/Cross';
import useReferralCodeSuggestions from '@cont/Checkout/useReferralCodeSuggestions';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import ReferralSuggestions from '@cont/Checkout/ReferralSuggestions';
import pushRoute from '@utils/pushRoute';
import { useRouter } from 'next/router';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import { getGoalDurationMetaInfo } from '@cont/Checkout/apiHelper';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import usePricings from '@cont/Checkout/usePricings';
import { segment } from '@actions/analytics';
import { CHECKOUT_PROCEED_TO_PAYMENT_SELECTED } from '@cont/Checkout/constants/segment';
import { plusProceedPaymentEventAnalytics } from '@cont/Checkout/utils';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe } from '@api/selectors/auth';
import isEmptyObject from '@utils/isEmptyObject';
import PlanDetailCard from '../BaseComponents/PlanDetailCard';
import {
  ApplyButton,
  ApplyText,
  InfoWrapper,
  ReferralBannerWrapper,
  ReferralRightWrapper,
  StyledDrawer,
  updateUrlWithReferral
} from '../NormalPlanSelectionFlow/NormalPlanSelectionPage';
import {
  defaultSelectedPricing,
  discountedPricingIfAvailable
} from '../planSelectionHelper';

const ExtendPlanSelectionFlowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;

const Title = styled(Typography)`
  width: 300px;
`;

const PlanDetailCardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
`;

interface DurationInfoType {
  durationInfo: Record<string, any>;
}

const ExtendPlanSelectionPage = ({
  preFilteredSubscriptionLists,
  setReferralCode,
  referralCode,
  selectedPlan,
  setSelectedPlan,
  purchaseDetails,
  referralError,
  validateReferralInProgress,
  goalDetails,
  referralValid,
  referralAllLoading,
  discountedPricings = []
}) => {
  const [planSelection, setPlanSelection] = useState<number>(0);
  const [isApplyCouponDrawerOpen, setIsApplyCouponDrawerOpen] =
    useState<boolean>(false);

  const { query } = useRouter();
  const { goalUID = '', goalSlug = '', referral_code = null } = query ?? {};

  const { pricings } = usePricings({ goalUID });

  const { goal = {} } = pricings ?? {};

  const { tax_breakdown = [] } = selectedPlan ?? {};

  const [goalDurationData, setGoalDurationData] =
    useState<Array<Record<string, any> | undefined>>();

  useEffect(() => {
    (async () => {
      const data = await getGoalDurationMetaInfo(goalUID);
      if (!data?.error) setGoalDurationData(data);
    })();
  }, [goalUID]);

  useEffect(() => {
    if (referral_code) setReferralCode(referral_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referral_code]);

  const {
    checkout_dates,
    checkout_dates_with_benefits: {
      ends_at = '',
      starts_at = ''
    } = checkout_dates ?? {}
  } = selectedPlan ?? {};

  const { isLoading: isMeDataLoading, isValidating: isMeDataValidating } =
    useMyInfo();
  const me = useMyInfo(getMe);

  const closeCouponsDrawer = useCallback(() => {
    setIsApplyCouponDrawerOpen(false);
  }, []);

  const { referralCodes = [] } = useReferralCodeSuggestions(
    preFilteredSubscriptionLists?.length > 0 &&
      preFilteredSubscriptionLists[0]?.uid
  );

  const firstReferralCode =
    referralCodes.length > 0
      ? referralCodes?.find((code) => code?.isActive === true)
      : {};

  useFirstRenderSkip(() => {
    if (!referralAllLoading || !referralCode)
      updateUrlWithReferral(goalSlug, goalUID, referralCode, referralValid);
  }, [goalSlug, goalUID, referralCode, referralValid, referralAllLoading]);

  const { referral: { discountPercent = '' } = {} } =
    purchaseDetails?.subscription ?? {};

  useEffect(() => {
    if (!isApplyCouponDrawerOpen)
      setSelectedPlan(preFilteredSubscriptionLists[0]);
  }, [
    isApplyCouponDrawerOpen,
    preFilteredSubscriptionLists,
    setSelectedPlan,
    referralCode
  ]);

  const isReferralAdded = referralValid && !!referralCode;

  const eventProps = useCheckoutEventProps(
    goalUID,
    discountedPricingIfAvailable(
      selectedPlan?.uid,
      isReferralAdded,
      discountedPricings
    ) || defaultSelectedPricing(selectedPlan?.uid, pricings),
    false
  );

  const subscription_price =
    tax_breakdown?.length > 0
      ? tax_breakdown?.find((tax) => {
          return tax?.label === 'Total';
        })
      : null;

  const evtPayload = {
    ...eventProps,
    referral_code: referralCode || 'NA',
    is_referral_successful: Boolean(referralCode) && !referralError,
    subscription_price: Number(subscription_price?.amount)
  };

  const isMeDataProcessing = isMeDataLoading && isMeDataValidating;

  useEffect(() => {
    if (!isMeDataProcessing)
      plusProceedPaymentEventAnalytics({
        ...evtPayload,
        referral_code,
        ...me,
        userId: me?.uid,
        ...goal,
        user_auth: !isEmptyObject(me)
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMeDataProcessing]);

  const handleExtendPlanSubmit = (plan) => {
    segment.track(CHECKOUT_PROCEED_TO_PAYMENT_SELECTED, evtPayload);
    pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]',
      query: {
        ...query,
        subscriptionUID: plan.uid
      }
    });
  };

  const getReferralCodeTitle = () => {
    if (referralValid && referralCode) return `Applied ${referralCode}`;
    if (firstReferralCode?.code) return `Use ${firstReferralCode?.code}`;
    return 'Have a referral code?';
  };

  return (
    <>
      <ExtendPlanSelectionFlowWrapper>
        <ReferralBannerWrapper>
          <Image
            src="newPlanSelection/referralTicket.svg"
            width={40}
            height={40}
            alt="Referral Ticket"
          />
          <ReferralRightWrapper>
            <InfoWrapper>
              <Typography variant="h6">{getReferralCodeTitle()}</Typography>
              {(firstReferralCode?.discountPercent || discountPercent) && (
                <Typography
                  variant="p3"
                  color={discountPercent && 'var(--color-i-brown-14)'}
                >
                  {referralValid && discountPercent
                    ? `Saving ${discountPercent}%`
                    : `Get ${firstReferralCode?.discountPercent}% off on all subscriptions`}
                </Typography>
              )}
            </InfoWrapper>
            {referralValid && referralCode ? (
              <Cross
                width={24}
                height={24}
                onClick={() => {
                  setReferralCode('');
                }}
              />
            ) : (
              <ApplyButton
                onClick={() => {
                  setIsApplyCouponDrawerOpen(true);
                }}
              >
                <ApplyText variant="p3">Apply</ApplyText>
                <RightChevron width={18} height={18} />
              </ApplyButton>
            )}
          </ReferralRightWrapper>
        </ReferralBannerWrapper>
        <PlanDetailCardWrapper>
          {preFilteredSubscriptionLists?.map((subscription, index) => {
            const goalDurationInfo = goalDurationData
              ? goalDurationData?.find((info) => {
                  return (
                    +info?.duration === subscription?.duration &&
                    info?.goalUid === goalUID
                  );
                })
              : [];
            return (
              <PlanDetailCard
                key={subscription.uid}
                subscription={subscription}
                index={index}
                planSelection={planSelection}
                onClick={() => {
                  setPlanSelection(index);
                  setSelectedPlan(subscription);
                }}
                onSubmit={(plan) => {
                  handleExtendPlanSubmit(plan);
                }}
                isReferralAdded={referralValid && !!referralCode}
                showDuration
                goalDurationInfo={goalDurationInfo as DurationInfoType}
                discountedPricings={discountedPricings}
              />
            );
          })}
        </PlanDetailCardWrapper>
      </ExtendPlanSelectionFlowWrapper>
      <StyledDrawer
        drawerState={isApplyCouponDrawerOpen}
        hideCloseButton
        onClose={closeCouponsDrawer}
      >
        <ReferralSuggestions
          onClose={closeCouponsDrawer}
          onApply={(referralCode) => {
            setReferralCode(referralCode);
          }}
          referralCode={referralCode}
          validateReferralInProgress={validateReferralInProgress}
          isReferralValid={referralCode && !referralError}
          referralMessage={referralError}
          subscriptionUid={selectedPlan?.uid}
        />
      </StyledDrawer>
    </>
  );
};

export default ExtendPlanSelectionPage;
